import { isMockAuthEnabled, signInMock, signOutMock } from "@/mocks/session";
import { useDispatch, useSelector } from "react-redux";
import { GlobalSelectors } from "./selectors";
import { globalActions } from "./slice";
import type { Session } from "./types";
import { useWeb3Auth } from "./Web3AuthProvider";

/**
 * The one way to read or change auth state anywhere in the app.
 *
 *   const { user, isAuthenticated, isLoading, login, logout } = useSession();
 *
 * `isLoading` is true until Web3Auth has finished initialising *and* its
 * asynchronous rehydration has settled — gate anything auth-dependent on it,
 * or a refresh will briefly render the signed-out view to a signed-in user.
 *
 * Session facts come from redux (so sagas and non-React code see the same
 * thing); `login` / `logout` come straight from the Web3Auth context.
 */
export const useSession = (): Session => {
  const dispatch = useDispatch();
  const web3auth = useWeb3Auth();

  const user = useSelector(GlobalSelectors.user);
  const isAuthenticated = useSelector(GlobalSelectors.isAuthenticated);
  const isLoading = useSelector(GlobalSelectors.isLoading);

  return {
    user,
    isAuthenticated,
    isLoading,
    login: async () => {
      // Mock mode has no Web3Auth to open; the signed-out state still has to be
      // reachable, so this toggles the fake session rather than being a no-op.
      if (isMockAuthEnabled) {
        const mockUser = signInMock();
        dispatch(
          globalActions.setSession({
            user: mockUser,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        );
        return;
      }
      if (!web3auth) return;
      await web3auth.login();
    },
    // Routed through the saga so the whole store is reset and storage cleared
    // in one place, however the logout was triggered (button, 401, expiry).
    logout: async () => {
      if (isMockAuthEnabled) signOutMock();
      dispatch(globalActions.logOut());
    },
  };
};
